import { Help, Logout, Security } from '@carbon/icons-react';
import { lazy, Suspense, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Banner, Button, CodeInput, FormField, Input, SkeletonText, Spinner } from '@/ui';
import { describeError } from '../api/errors';
import { useServices, useSession } from '../AppServicesContext';
import { useHelp } from '../shell/HelpContext';
import s from '../layouts/Layout.module.css';
import { AUTH_PATHS, safeNextPath } from './paths';

const MfaSetup = lazy(() => import('./MfaSetup'));

const CODE_LENGTH = 6;

/**
 * Второй фактор (ПОРТ-21). Открывается при MFA_REQUIRED: код из приложения-аутентификатора или
 * код восстановления; если приложение ещё не подключено — подключение с QR-кодом.
 */
export function MfaChallengePage() {
  const { t } = useTranslation('auth');
  const { t: tErrors, i18n } = useTranslation('errors');
  const { session } = useServices();
  const { state } = useSession();
  const help = useHelp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const [mode, setMode] = useState<'app' | 'recovery'>('app');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.status === 'loading') return <Spinner size="lg" label={t('mfa.checking')} />;
  if (state.status === 'anonymous') return <Navigate to={AUTH_PATHS.login} replace />;
  if (state.status === 'authenticated') return <Navigate to={next} replace />;

  const enrolled = state.user?.mfa.enrolled ?? true;

  const verify = async (proof: { code: string } | { recoveryCode: string }) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await session.verifyMfa(proof);
      navigate(next, { replace: true });
    } catch (caught) {
      setError(describeError(caught, tErrors, i18n).description);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = (value: string) => {
    if (value.length === CODE_LENGTH) void verify({ code: value });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (mode === 'app') {
      submitCode(code);
      return;
    }
    const value = recoveryCode.trim();
    if (!value) {
      setRecoveryError(t('mfa.recoveryEmpty'));
      return;
    }
    setRecoveryError(null);
    void verify({ recoveryCode: value });
  };

  const switchMode = (nextMode: 'app' | 'recovery') => {
    setMode(nextMode);
    setError(null);
    setRecoveryError(null);
  };

  return (
    <div className={s.authCard}>
      <span className={s.authIcon} aria-hidden="true">
        <Security size={24} />
      </span>
      <div className={s.authHeading}>
        <h1 className={s.authTitle}>{enrolled ? t('mfa.title') : t('mfa.setup.title')}</h1>
        <p className={s.authText}>{enrolled ? t('mfa.description') : t('mfa.setup.description')}</p>
      </div>
      {enrolled ? (
        <>
          {error ? (
            <Banner tone="error" announce>
              {error}
            </Banner>
          ) : null}
          <form className={s.authForm} onSubmit={handleSubmit} noValidate>
            {mode === 'app' ? (
              <FormField label={t('mfa.codeLabel')} hint={t('mfa.codeHint')}>
                <CodeInput length={CODE_LENGTH} value={code} onChange={setCode} onComplete={submitCode} disabled={busy} autoFocus />
              </FormField>
            ) : (
              <FormField label={t('mfa.recoveryLabel')} hint={t('mfa.recoveryHint')} error={recoveryError ?? undefined}>
                <Input value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} autoComplete="off" spellCheck={false} disabled={busy} autoFocus />
              </FormField>
            )}
            <Button type="submit" size="lg" fullWidth loading={busy} disabled={mode === 'app' && code.length !== CODE_LENGTH}>
              {t('mfa.submit')}
            </Button>
            <Button variant="ghost" onClick={() => switchMode(mode === 'app' ? 'recovery' : 'app')}>
              {mode === 'app' ? t('mfa.useRecovery') : t('mfa.useApp')}
            </Button>
          </form>
          {mode === 'recovery' ? <p className={s.authText}>{t('mfa.lostAccess')}</p> : null}
        </>
      ) : (
        <Suspense fallback={<SkeletonText lines={6} />}>
          <MfaSetup
            onDone={async () => {
              await session.completeMfa();
              navigate(next, { replace: true });
            }}
          />
        </Suspense>
      )}
      <div className={s.authFooter}>
        <Button variant="ghost" icon={Help} onClick={() => help.open({ errorCode: 'MFA_REQUIRED' })}>
          {mode === 'recovery' ? t('mfa.contactSupport') : t('mfa.noAccess')}
        </Button>
        <Button variant="ghost" icon={Logout} onClick={() => void session.signOut()}>
          {t('mfa.signOut')}
        </Button>
      </div>
    </div>
  );
}
